import { DateTime } from 'luxon';
import React from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';

import 'source/packages/styles/common.scss';

import { date as commonDate } from '@finley-technologies/common';
import { DataTapeField } from '@finley-technologies/finley-types';

import { colors, transparency } from 'packages/common-frontend/colors';
import {
  ChartTimePeriod,
  dateFormatter,
  PortfolioChartFormat,
  tooltipFormatter,
  valueTypeToTickFormatterMap,
} from 'packages/portfolio/ui/shared/portfolioChartUtils';

const getPortfolioChartFormatType = (
  baseFieldType: DataTapeField.NumericType,
): PortfolioChartFormat => {
  switch (baseFieldType) {
    case 'currency':
    case 'optionalCurrency':
      return 'currency';
    case 'percentage':
    case 'optionalPercentage':
      return 'percentage';
    case 'float':
    case 'optionalFloat':
      return 'float';
    default:
      return 'integer';
  }
};

const chartColor = `${colors.chart.lilac}${transparency[50]}`;

type NumericBaseFieldChartProps = {
  historicalRecords: any[];
  baseFieldName: string;
  baseFieldType: DataTapeField.NumericType;
  selectedTimePeriod: ChartTimePeriod;
};
export const NumericBaseFieldChart = ({
  historicalRecords,
  baseFieldName,
  baseFieldType,
  selectedTimePeriod,
}: NumericBaseFieldChartProps) => {
  const customTooltipFormatter = (value: number, name: string) =>
    tooltipFormatter(
      value,
      name.slice(3),
      getPortfolioChartFormatType(baseFieldType),
    );

  return (
    <ResponsiveContainer width="100%" height={500}>
      <AreaChart
        data={historicalRecords}
        margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
      >
        <CartesianGrid vertical={false} />
        <defs>
          <linearGradient id={baseFieldName} x1="0" y1="0" x2="0" y2="0.7">
            <stop stopColor={chartColor} />
            <stop offset="1" stopColor="white" stopOpacity="0.15" />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="reportingdate"
          height={70}
          tick={{ fontWeight: 600 }}
          tickFormatter={(value: any) =>
            dateFormatter({ date: value, timePeriod: selectedTimePeriod })
          }
          axisLine={false}
          tickLine={false}
          tickMargin={10}
        />

        <YAxis
          tickFormatter={
            valueTypeToTickFormatterMap[
              getPortfolioChartFormatType(baseFieldType)
            ]
          }
          tick={{ fontWeight: 600 }}
          axisLine={false}
          tickLine={false}
          orientation="left"
        />

        <Tooltip
          formatter={customTooltipFormatter}
          itemStyle={{ color: colors.gray[500] }}
          cursor={{ stroke: 'none' }}
          contentStyle={{ fontSize: '12px' }}
          labelFormatter={(label) =>
            commonDate.dateToHumanDateString(DateTime.fromISO(label).toJSDate())
          }
          labelStyle={{ fontWeight: 'bold' }}
        />
        <Area
          type="monotone"
          dataKey={`bf.${baseFieldName}`}
          stroke={chartColor}
          strokeWidth="1px"
          fillOpacity={1}
          fill={`url(#${baseFieldName})`} // the fill id refers to the linearGradient id defined above
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};
